import { useCallback, useMemo, useState } from "react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import ProgressBar from "ds/components/ProgressBar";
import Card from "ds/components/Card";
import Banner from "ds/components/Banner";
import TextEllipsis from "ds/components/TextEllipsis";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import { UserGuideStepData } from "shared/UserGuides/gql";
import { getErrorMessage } from "shared/UserGuides/utils";
import {
  getHardcodedUserGuideGroupIconAndColor,
  randomIconAndColor,
} from "shared/UserGuides/mocks";
import FeedbackButton from "components/FeedbackButton";

import useAssistantPanelLearnStore from "../../../useStore";
import { showAssistantPanelLearnCancelConfirmationModal } from "./CancelConfirmationModal";
import styles from "./styles.module.css";

type AssistantPanelLearnActiveGuideViewStepProps = {
  guideTitle: string;
  currentStep: UserGuideStepData;
  currentStepIndex: number;
  totalSteps: number;
  onCompleteClick: () => void;
  onCompleteSuccess?: () => void;
};

const AssistantPanelLearnActiveGuideViewStep = ({
  guideTitle,
  currentStep,
  currentStepIndex,
  totalSteps,
  onCompleteClick,
  onCompleteSuccess,
}: AssistantPanelLearnActiveGuideViewStepProps) => {
  const { setNavigationState, navigationState } = useAssistantPanelLearnStore();
  const { moveToNextStep, moveToPreviousStep, completeUserGuide, abandonUserGuide } =
    useUserGuideProgressMutations({ propagateError: true, suppressFlashError: true });

  const [isCancelling, setIsCancelling] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPreviousLoading, setIsPreviousLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [stepErrorMessage, setStepErrorMessage] = useState<string>();

  const isAnyActionLoading = isCancelling || isNextLoading || isPreviousLoading || isCompleting;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex + 1 === totalSteps;

  const handleCancel = useCallback(() => {
    showAssistantPanelLearnCancelConfirmationModal({
      guideTitle,
      onConfirm: async () => {
        setStepErrorMessage(undefined);
        setIsCancelling(true);
        try {
          await abandonUserGuide();
          if (navigationState.level === "guide") {
            setNavigationState({ level: "chapters", groupId: navigationState.groupId });
          } else {
            setNavigationState({ level: "groups" });
          }
        } catch (error) {
          setStepErrorMessage(getErrorMessage(error));
        } finally {
          setIsCancelling(false);
        }
      },
    });
  }, [guideTitle, setNavigationState, abandonUserGuide, navigationState]);

  const handleNextStep = useCallback(async () => {
    setStepErrorMessage(undefined);
    setIsNextLoading(true);
    try {
      await moveToNextStep();
    } catch (error) {
      setStepErrorMessage(getErrorMessage(error));
    } finally {
      setIsNextLoading(false);
    }
  }, [moveToNextStep]);

  const handlePreviousStep = useCallback(async () => {
    setStepErrorMessage(undefined);
    setIsPreviousLoading(true);
    try {
      await moveToPreviousStep();
    } catch (error) {
      setStepErrorMessage(getErrorMessage(error));
    } finally {
      setIsPreviousLoading(false);
    }
  }, [moveToPreviousStep]);

  const handleCompleteGuide = useCallback(async () => {
    setStepErrorMessage(undefined);
    setIsCompleting(true);
    onCompleteClick();

    try {
      await completeUserGuide();
      onCompleteSuccess?.();
    } catch (error) {
      setStepErrorMessage(getErrorMessage(error));
    } finally {
      setIsCompleting(false);
    }
  }, [completeUserGuide, onCompleteClick, onCompleteSuccess]);

  const icon = useMemo(() => {
    if (navigationState.level === "guide" || navigationState.level === "chapters") {
      const { groupId } = navigationState;
      if (groupId) {
        return getHardcodedUserGuideGroupIconAndColor(groupId).icon;
      }
    }

    // if from some reason we are in the groups level, fallback to a random icon and color
    return randomIconAndColor().icon;
  }, [navigationState]);

  return (
    <Box direction="column" gap="large" grow="1" padding="x-large" scrollable>
      <Box align="center" justify="between" fullWidth gap="medium">
        <Box align="center" gap="small" className={styles.guideTitle} padding="small">
          <Icon src={icon} />
          <TextEllipsis tooltip={guideTitle}>
            {(props) => (
              <Typography {...props} tag="span" variant="p-body4">
                {guideTitle}
              </Typography>
            )}
          </TextEllipsis>
        </Box>

        <Box grow="1" align="center" gap="medium" className={styles.progressBar}>
          <ProgressBar
            current={currentStepIndex + 1}
            max={totalSteps}
            fullWidth
            ariaLabel="Guide progress"
          />
        </Box>
      </Box>

      <Card contentGap="large" className={styles.card}>
        <Typography tag="h3" variant="p-t7">
          {currentStep.title}
        </Typography>

        {stepErrorMessage && <Banner variant="danger">{stepErrorMessage}</Banner>}

        <Typography tag="div" variant="p-body3">
          <MarkdownAssistantPanel>{currentStep.instruction}</MarkdownAssistantPanel>
        </Typography>

        {currentStep.hint && (
          <Banner variant="info">
            <MarkdownAssistantPanel>{currentStep.hint}</MarkdownAssistantPanel>
          </Banner>
        )}

        <Box direction="row" gap="medium" align="center" justify="between">
          <Button
            variant="secondary"
            size="small"
            onPress={handleCancel}
            disabled={isAnyActionLoading}
            loading={isCancelling}
          >
            Cancel
          </Button>

          <Box direction="row" gap="medium" align="center">
            <Button
              variant="secondary"
              size="small"
              onPress={handlePreviousStep}
              disabled={isAnyActionLoading || isFirstStep}
              loading={isPreviousLoading}
            >
              Previous step
            </Button>
            {!isLastStep && (
              <Button
                variant="primary"
                size="small"
                onPress={handleNextStep}
                disabled={isAnyActionLoading}
                loading={isNextLoading}
              >
                Next step
              </Button>
            )}
            {isLastStep && (
              <Button
                variant="primary"
                size="small"
                onPress={handleCompleteGuide}
                disabled={isAnyActionLoading}
                loading={isCompleting}
              >
                Complete
              </Button>
            )}
          </Box>
        </Box>

        <Box className={styles.needHelpRow} align="center">
          <FeedbackButton id="user-guide-step-feedback" variant="ghost" size="small" />
        </Box>
      </Card>
    </Box>
  );
};

export default AssistantPanelLearnActiveGuideViewStep;
